import React, { useState } from "react";
import styles from "./Users.module.scss";
import CustomHeader from "@/components/CustomHeader/CustomHeader";
import Table from "@/components/Table/Table";
import Tabs from "@/components/Tabs/Tabs";
import Chip, { type ChipVariant } from "@/components/Chip/Chip";
import type { TableColumn, SortOrder as DataTableSortOrder } from "react-data-table-component";
import type { Profile, ProfileSortBy } from "@/services/api/profile";
import type { IncompleteUser, IncompleteUserSortBy } from "@/services/api/incompleteUsers";
import type { CompleteUser, CompleteUserSortBy } from "@/services/api/completeUsers";
import { useSearchPagination } from "@/hooks/useSearchPagination";
import { useProfiles } from "@/hooks/queries/useProfiles";
import { useIncompleteUsers } from "@/hooks/queries/useIncompleteUsers";
import { useCompleteUsers } from "@/hooks/queries/useCompleteUsers";
import { dateConverter } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

const TAB_NAMES = ["All Users", "Complete", "Incomplete"] as const;

const MISSING_FIELDS: { key: keyof IncompleteUser; label: string }[] = [
  { key: "has_avatar", label: "avatar" },
  { key: "has_bio", label: "bio" },
  { key: "has_roles", label: "roles" },
  { key: "has_locations", label: "locations" },
  { key: "phone_verified", label: "phone" },
  { key: "has_active_plan", label: "plan" },
];

const PLAN_CHIP_VARIANTS: Record<string, ChipVariant> = {
  founders: "success",
  trial: "warning",
  "pro alumni": "neutral",
  core: "info",
  pro: "info",
};

const PLAN_ALIASES: Record<string, string> = {
  founder: "founders",
};

const renderPlan = (plan?: string | null) => {
  if (!plan) return <span className={styles.mutedText}>—</span>;
  const normalized = plan.trim().toLowerCase().replace(/[_-]+/g, " ");
  const key = PLAN_ALIASES[normalized] ?? normalized;
  const label = key.replace(/\b\w/g, (c) => c.toUpperCase());
  return <Chip variant={PLAN_CHIP_VARIANTS[key] ?? "neutral"}>{label}</Chip>;
};

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [incompleteSortBy, setIncompleteSortBy] = useState<IncompleteUserSortBy>("created_at");
  const [incompleteSortOrder, setIncompleteSortOrder] = useState<"asc" | "desc">("desc");
  const [completeSortBy, setCompleteSortBy] = useState<CompleteUserSortBy>("created_at");
  const [completeSortOrder, setCompleteSortOrder] = useState<"asc" | "desc">("desc");
  const [usersSortBy, setUsersSortBy] = useState<ProfileSortBy>("created_at");
  const [usersSortOrder, setUsersSortOrder] = useState<"asc" | "desc">("desc");

  const {
    debouncedSearch,
    search,
    setSearch,
    pageSelected,
    setPageSelected,
    rowsPerPageValue,
    setRowsPerPageValue,
    clearSearch,
  } = useSearchPagination();

  const { data, isLoading: isFetchingAdmins, isFetching } = useProfiles({
    page: pageSelected,
    limit: rowsPerPageValue,
    search: debouncedSearch,
    sortBy: usersSortBy,
    sortOrder: usersSortOrder,
    enabled: activeTab === 0,
  });

  const {
    data: incompleteData,
    isLoading: isLoadingIncomplete,
    isFetching: isFetchingIncomplete,
  } = useIncompleteUsers({
    page: pageSelected,
    limit: rowsPerPageValue,
    search: debouncedSearch,
    sortBy: incompleteSortBy,
    sortOrder: incompleteSortOrder,
    enabled: activeTab === 2,
  });

  const {
    data: completeData,
    isLoading: isLoadingComplete,
    isFetching: isFetchingComplete,
  } = useCompleteUsers({
    page: pageSelected,
    limit: rowsPerPageValue,
    search: debouncedSearch,
    sortBy: completeSortBy,
    sortOrder: completeSortOrder,
    enabled: activeTab === 1,
  });

  const adminAccounts = data?.items ?? [];
  const totalCount = data?.total ?? 0;

  const completeUsers = completeData?.items ?? [];
  const completeTotalCount = completeData?.total ?? 0;

  const incompleteUsers = incompleteData?.items ?? [];
  const incompleteTotalCount = incompleteData?.total ?? 0;

  const adminColumns: TableColumn<Profile>[] = [
    {
      id: "display_name",
      name: "Name",
      width: "16%",
      selector: (row) => row.display_name,
      sortable: true,
    },
    {
      name: "Recipient",
      width: "16%",
      selector: (row) => row.email || row.phone || "",
    },
    {
      id: "plan",
      name: "Plan",
      width: "12%",
      cell: (row) => renderPlan(row.plan),
      sortable: true,
    },
    {
      id: "gig_count",
      name: "Gigs",
      width: "10%",
      selector: (row) => row.gig_count,
      sortable: true,
    },
    {
      id: "ww_count",
      name: "WW",
      width: "10%",
      selector: (row) => row.ww_count ?? 0,
      sortable: true,
    },
    {
      id: "ic_count",
      name: "IC",
      width: "10%",
      selector: (row) => row.ic_count ?? 0,
      sortable: true,
    },
    {
      id: "dm_count",
      name: "DMs",
      width: "10%",
      selector: (row) => row.dm_count ?? 0,
      sortable: true,
    },
    {
      id: "created_at",
      name: "Created At",
      width: "14%",
      selector: (row) => dateConverter(row.created_at),
      sortable: true,
    },
  ];

  const completeColumns: TableColumn<CompleteUser>[] = [
    { name: "Name", width: "25%", selector: (row) => row.display_name || "—" },
    { name: "Recipient", width: "25%", selector: (row) => row.email || row.phone || "" },
    {
      id: "plan",
      name: "Plan",
      width: "15%",
      cell: (row) => renderPlan(row.plan),
      sortable: true,
    },
    {
      name: "Phone Verified",
      width: "15%",
      selector: (row) => (row.phone_verified ? "Yes" : "No"),
    },
    {
      id: "created_at",
      name: "Signed Up",
      width: "20%",
      selector: (row) => dateConverter(row.created_at),
      sortable: true,
    },
  ];

  const incompleteColumns: TableColumn<IncompleteUser>[] = [
    { name: "Name", width: "20%", selector: (row) => row.display_name || "—" },
    { name: "Recipient", width: "20%", selector: (row) => row.email || row.phone || "" },
    {
      id: "plan",
      name: "Plan",
      width: "10%",
      cell: (row) => renderPlan(row.plan),
      sortable: true,
    },
    {
      id: "missing",
      name: "Missing",
      width: "30%",
      sortable: true,
      cell: (row) => {
        const missing = MISSING_FIELDS.filter((field) => !row[field.key]);
        if (!missing.length) return <span className={styles.mutedText}>—</span>;
        return (
          <div className={styles.missingChips}>
            {missing.map((field) => (
              <Chip key={field.key} variant="warning">
                {field.label}
              </Chip>
            ))}
          </div>
        );
      },
    },
    {
      id: "created_at",
      name: "Signed Up",
      width: "20%",
      selector: (row) => dateConverter(row.created_at),
      sortable: true,
    },
  ];

  return (
    <div className={styles.main}>
      <CustomHeader
        title="Users"
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search name, email or phone"
      />

      <Tabs
        tabs={TAB_NAMES}
        activeIndex={activeTab}
        onChange={(index) => {
          setActiveTab(index);
          setPageSelected(1);
          clearSearch();
        }}
        className={styles.tabsWrapper}
      />

      {activeTab === 0 && (
        <Table
          columns={adminColumns}
          data={adminAccounts}
          className={styles.candidatesTable ?? ""}
          progressPending={isFetchingAdmins || isFetching}
          pointerOnHover
          sortServer
          defaultSortFieldId="created_at"
          defaultSortAsc={false}
          fixedHeaderScrollHeight={"calc(100vh - 19rem)"}
          onSort={(column, sortDirection: DataTableSortOrder) => {
            if (column.id) setUsersSortBy(column.id as ProfileSortBy);
            setUsersSortOrder(sortDirection);
          }}
          onRowClicked={(row) => {
            navigate(`/users/${row.id}`);
          }}
          paginationProps={{
            isPagination: true,
            tableName: "Accounts",
            pageSelected,
            totalCount,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />
      )}

      {activeTab === 1 && (
        <Table
          columns={completeColumns}
          data={completeUsers}
          className={styles.candidatesTable ?? ""}
          progressPending={isLoadingComplete || isFetchingComplete}
          pointerOnHover
          sortServer
          defaultSortFieldId="created_at"
          defaultSortAsc={false}
          fixedHeaderScrollHeight={"calc(100vh - 19rem)"}
          onSort={(column, sortDirection: DataTableSortOrder) => {
            if (column.id) setCompleteSortBy(column.id as CompleteUserSortBy);
            setCompleteSortOrder(sortDirection);
          }}
          onRowClicked={(row) => {
            navigate(`/users/${row.id}`);
          }}
          paginationProps={{
            isPagination: true,
            tableName: "Complete users",
            pageSelected,
            totalCount: completeTotalCount,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />
      )}

      {activeTab === 2 && (
        <Table
          columns={incompleteColumns}
          data={incompleteUsers}
          className={styles.candidatesTable ?? ""}
          progressPending={isLoadingIncomplete || isFetchingIncomplete}
          pointerOnHover
          sortServer
          defaultSortFieldId="created_at"
          defaultSortAsc={false}
          fixedHeaderScrollHeight={"calc(100vh - 19rem)"}
          onSort={(column, sortDirection: DataTableSortOrder) => {
            if (column.id) setIncompleteSortBy(column.id as IncompleteUserSortBy);
            setIncompleteSortOrder(sortDirection);
          }}
          onRowClicked={(row) => {
            navigate(`/users/${row.id}`);
          }}
          paginationProps={{
            isPagination: true,
            tableName: "Incomplete users",
            pageSelected,
            totalCount: incompleteTotalCount,
            rowsPerPageValue,
            setRowsPerPageValue,
            setPageSelected,
          }}
        />
      )}
    </div>
  );
};

export default Users;
